import type { PolygonWithHoles, Ring, Segment, Vec2 } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import { difference, fromRing, intersection } from '../../geo/clip';
import { distancePointSegment, ensureCCW } from '../../geo/polygon';
import type { SeamRun } from './farRingSeam';

// Clipping rounds to the millimetre, so pieces cut from one polygon share edges this closely.
const SHARED_EDGE_M = 0.01;

export interface FarRingSector {
  readonly run: SeamRun;
  readonly polygons: readonly PolygonWithHoles[];
}

interface LandSectors {
  readonly sectors: readonly FarRingSector[];
  readonly unclaimed: readonly PolygonWithHoles[];
}

function extrude(p: Vec2, centre: Vec2, reach: number): Vec2 {
  const [dx, dy] = [p[0] - centre[0], p[1] - centre[1]];
  const scale = (Math.hypot(dx, dy) + reach) / Math.hypot(dx, dy);
  return [centre[0] + dx * scale, centre[1] + dy * scale];
}

// The far ring extrudes the bounds radially from their centre, so the wedge a run sweeps outward
// holds the far land that continues it, up to the rivers that cross the far ring.
function wedgeOf(run: SeamRun, centre: Vec2, reach: number): PolygonWithHoles {
  const outward = run.points.map((p) => extrude(p, centre, reach)).reverse();
  return fromRing(ensureCCW([...run.points, ...outward]));
}

function edgesOf(ring: Ring): Segment[] {
  return ring.map((a, i) => ({ a, b: ring[(i + 1) % ring.length]! }));
}

function midpoint({ a, b }: Segment): Vec2 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function onBoundary(p: Vec2, polygon: PolygonWithHoles): boolean {
  return [polygon.outer, ...polygon.holes].some((ring) =>
    edgesOf(ring).some((edge) => distancePointSegment(p, edge) < SHARED_EDGE_M)
  );
}

function continuesInto(run: SeamRun, land: PolygonWithHoles): boolean {
  const [a, b] = run.points;
  return a !== undefined && b !== undefined && onBoundary(midpoint({ a, b }), land);
}

function sharedLength(piece: PolygonWithHoles, polygons: readonly PolygonWithHoles[]): number {
  return edgesOf(piece.outer).reduce(
    (sum, edge) =>
      polygons.some((polygon) => onBoundary(midpoint(edge), polygon))
        ? sum + Math.hypot(edge.b[0] - edge.a[0], edge.b[1] - edge.a[1])
        : sum,
    0
  );
}

// Where a river leaves the bounds at a slant, the far land beside it lies outside every radial
// wedge; it joins the sector it borders most, which is the land it continues.
function adopt(
  sectors: readonly FarRingSector[],
  leftovers: readonly PolygonWithHoles[]
): LandSectors {
  const owners = leftovers.map((piece) => {
    const shared = sectors.map((sector) => sharedLength(piece, sector.polygons));
    const most = Math.max(...shared);
    return most > 0 ? shared.indexOf(most) : -1;
  });
  return {
    sectors: sectors.map((sector, i) => ({
      run: sector.run,
      polygons: [...sector.polygons, ...leftovers.filter((_, k) => owners[k] === i)],
    })),
    unclaimed: leftovers.filter((_, k) => owners[k] === -1),
  };
}

// Each far land polygon lies between two far-ring rivers, so a run's wedge is cut from the land it
// borders only: land across a river continues the pieces on that side.
function sectorsOfLand(
  land: PolygonWithHoles,
  runs: readonly SeamRun[],
  centre: Vec2,
  reach: number
): LandSectors {
  const sectors = runs
    .filter((run) => continuesInto(run, land))
    .map((run) => ({ run, polygons: intersection([wedgeOf(run, centre, reach)], [land]) }));
  const leftovers = difference(
    [land],
    sectors.flatMap((sector) => sector.polygons)
  );
  return adopt(sectors, leftovers);
}

export function farRingSectors(
  site: SiteModel,
  runs: readonly SeamRun[],
  farRadius: number
): { readonly sectors: readonly FarRingSector[]; readonly remainder: readonly PolygonWithHoles[] } {
  const { minX, minY, maxX, maxY } = site.bounds;
  const centre: Vec2 = [(minX + maxX) / 2, (minY + maxY) / 2];
  const parts = site.farRing.land.map((land) => sectorsOfLand(land, runs, centre, 2 * farRadius));
  return {
    sectors: parts.flatMap((part) => part.sectors),
    remainder: parts.flatMap((part) => part.unclaimed),
  };
}
